import React, { createContext, FC, useEffect, useMemo, useRef, useState } from "react";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { useComplianceConnectFilterOptions } from "@app/compliance-connect/common/useComplianceConnectFilterOptions";

export interface ComplianceConnectApiFilters {
    networkNameSearch: string;
    serviceAreaIds: string[];
    specialtyGroupCodes: string[];
}

export interface ComplianceConnectFiltersContextValues {
    apiReadyFilters: ComplianceConnectApiFilters;
    applyFilters: () => void;
    isDashboardLoading: boolean;
    isFilterOptionsError: boolean;
    isFilterOptionsLoading: boolean;
    isFilterSelectionEmpty: boolean;
    networkNameSearch: string;
    resetFilters: () => void;
    selectedServiceAreas: string[];
    selectedSpecialties: DropdownItem<string>[];
    serviceAreaOptions: NestingParentDropdownItem[];
    setIsDashboardLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setNetworkNameSearch: (value: string) => void;
    setSelectedServiceAreas: (keys: string[]) => void;
    setSelectedSpecialties: (value: DropdownItem<string>[]) => void;
    specialtyOptions: DropdownItem<string>[];
}

const defaultApiReadyFilters: ComplianceConnectApiFilters = {
    networkNameSearch: "",
    serviceAreaIds: [],
    specialtyGroupCodes: [],
};

const initialValues: ComplianceConnectFiltersContextValues = {
    apiReadyFilters: defaultApiReadyFilters,
    applyFilters: () => null,
    isDashboardLoading: true,
    isFilterOptionsError: false,
    isFilterOptionsLoading: true,
    isFilterSelectionEmpty: false,
    networkNameSearch: "",
    resetFilters: () => null,
    selectedServiceAreas: [],
    selectedSpecialties: [],
    serviceAreaOptions: [],
    setIsDashboardLoading: () => null,
    setNetworkNameSearch: () => null,
    setSelectedServiceAreas: () => null,
    setSelectedSpecialties: () => null,
    specialtyOptions: [],
};

export const ComplianceConnectFiltersContext = createContext(initialValues);

const ComplianceConnectFiltersContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
    const [apiReadyFilters, setApiReadyFilters] = useState<ComplianceConnectApiFilters>(defaultApiReadyFilters);
    const [isDashboardLoading, setIsDashboardLoading] = useState(true);
    const [networkNameSearch, setNetworkNameSearch] = useState("");
    const [selectedServiceAreas, setSelectedServiceAreas] = useState<string[]>([]);
    const [selectedSpecialties, setSelectedSpecialties] = useState<DropdownItem<string>[]>([]);
    const [isFilterSelectionEmpty, setIsFilterSelectionEmpty] = useState(false);
    const { filterOptions, filterOptionsError: isFilterOptionsError, isLoading: isFilterOptionsLoading } = useComplianceConnectFilterOptions();

    const serviceAreaOptions: NestingParentDropdownItem[] = useMemo(() => (filterOptions?.serviceAreas ?? []).map((state) => ({
        checkboxStatus: CheckboxStateEnum.Unchecked,
        children: (state.children ?? []).map((county) => ({ key: county.value, name: county.displayName })),
        key: state.value,
        name: state.displayName,
    })), [filterOptions]);

    const allServiceAreaKeys = useMemo(
        () => serviceAreaOptions.flatMap((state) => state.children.map((county) => county.key)),
        [serviceAreaOptions],
    );

    const specialtyOptions: DropdownItem<string>[] = (filterOptions?.specialties ?? [])
        .map((o) => ({ key: o.value, name: o.displayName }));

    const hasInitializedSelections = useRef(false);

    useEffect(() => {
        if (!hasInitializedSelections.current && !isFilterOptionsLoading && filterOptions != null) {
            hasInitializedSelections.current = true;
            setSelectedServiceAreas(allServiceAreaKeys);
            setSelectedSpecialties(specialtyOptions);
        }
    }, [isFilterOptionsLoading, filterOptions, allServiceAreaKeys, specialtyOptions]);

    const applyFilters = () => {
        setIsDashboardLoading(true);

        const allServiceAreaKeysSet = new Set(allServiceAreaKeys);
        const selectedServiceAreaKeysSet = new Set(selectedServiceAreas);
        const isEveryServiceAreaSelected = selectedServiceAreaKeysSet.size === allServiceAreaKeysSet.size
            && [...selectedServiceAreaKeysSet].every((key) => allServiceAreaKeysSet.has(key));

        const allSpecialtyKeysSet = new Set(specialtyOptions.map((o) => o.key));
        const selectedSpecialtyKeysSet = new Set(selectedSpecialties.map((s) => s.key));
        const isEverySpecialtySelected = selectedSpecialtyKeysSet.size === allSpecialtyKeysSet.size
            && [...selectedSpecialtyKeysSet].every((key) => allSpecialtyKeysSet.has(key));

        // A dropdown with zero available options is a degenerate "nothing to select" state, not the
        // user explicitly narrowing to nothing — only treat a deselect-all as a real empty selection
        // when there were options to deselect in the first place.
        const hasEmptyServiceAreaSelection = allServiceAreaKeys.length > 0 && selectedServiceAreas.length === 0;
        const hasEmptySpecialtySelection = specialtyOptions.length > 0 && selectedSpecialties.length === 0;
        setIsFilterSelectionEmpty(hasEmptyServiceAreaSelection || hasEmptySpecialtySelection);

        setApiReadyFilters({
            networkNameSearch,
            // selecting every option is functionally the same as selecting none, so send an empty list
            serviceAreaIds: isEveryServiceAreaSelected ? [] : selectedServiceAreas,
            specialtyGroupCodes: isEverySpecialtySelected ? [] : selectedSpecialties.map((s) => s.key),
        });
    };

    const resetFilters = () => {
        setNetworkNameSearch("");
        setSelectedServiceAreas(allServiceAreaKeys);
        setSelectedSpecialties(specialtyOptions);
        setApiReadyFilters(defaultApiReadyFilters);
        setIsFilterSelectionEmpty(false);
    };

    return (
        <ComplianceConnectFiltersContext.Provider value={{
            apiReadyFilters,
            applyFilters,
            isDashboardLoading,
            isFilterOptionsError,
            isFilterOptionsLoading,
            isFilterSelectionEmpty,
            networkNameSearch,
            resetFilters,
            selectedServiceAreas,
            selectedSpecialties,
            serviceAreaOptions,
            setIsDashboardLoading,
            setNetworkNameSearch,
            setSelectedServiceAreas,
            setSelectedSpecialties,
            specialtyOptions,
        }}>
            {children}
        </ComplianceConnectFiltersContext.Provider>
    );
};

export default ComplianceConnectFiltersContextProvider;
